import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import queryString from "query-string";

import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  useTheme
} from "@mui/material";

import AccountAccessLayout from "../layouts/AccountAccessLayout";

import AccountCreationFlow from "./AccountCreationFlow";

import { useExistingIndividualData } from "../../helpers/components/accountAccess";
import { analyticsPage } from "../../helpers/analytics";

const myAclymateAccountCreationBulletPoints = [
  {
    title: "Take the free myAclymate Quiz",
    subtitle:
      "Get started by taking the myAclymate Quiz to calculate your personal carbon footprint and get a breakdown of your emissions for no cost."
  },
  {
    title: "Select your offsets",
    subtitle:
      "After you get the results from your quiz, select the cause you want to support with your offsets."
  },
  {
    title: "Reduce your emissions",
    subtitle:
      "Using your emissions results, identify places where you can reduce emissions and update your information as you reduce."
  }
];

const companyAccountCreationBulletPoints = [
  {
    title: `Access Your Dashboard`,
    subtitle: `Create your account in minutes and access your dashboard immediately.`
  },
  {
    title: "See Your Emissions Structure",
    subtitle:
      "Understand how Scope 1, 2, and 3 emissions are organized without setup work."
  },
  {
    title: "Get an Initial Estimate",
    subtitle:
      "We use publicly available information to generate a starting emissions estimate you can refine over time."
  }
];

const AccountCreation = () => {
  const { palette } = useTheme();
  const { accountCreationType } = useParams();

  const [individualData, individualDataLoading] = useExistingIndividualData();
  const [errorMsg, setErrorMsg] = useState("");

  const newAccountQueries = queryString.parse(window.location.search);
  const {
    companyId,
    email: storedEmail,
    companyName
  } = newAccountQueries || {};
  const isNewCompanyAdmin = !!companyId && !!storedEmail;
  const newCompanyAdminProp = isNewCompanyAdmin && newAccountQueries;

  useEffect(() => {
    analyticsPage("account-creation");
  }, []);

  const buildPageTitleSubtitle = () => {
    if (accountCreationType !== "company") {
      return {
        title: "Track and offset your footprint",
        subtitle:
          "Create your free myAclymate account to track and offset your individual footprint.",
        imagePath: "/images/platform/login-image.jpg",
        bulletPoints: myAclymateAccountCreationBulletPoints
      };
    }

    if (isNewCompanyAdmin) {
      return {
        title: `Join ${companyName} on Aclymate to help them save the world.`,
        subtitle: `With Aclymate you'll be able to help ${companyName} easily track, reduce, and offsets their carbon footprint.`,
        imagePath: "/images/platform/account-access-header.jpg",
        bulletPoints: companyAccountCreationBulletPoints
      };
    }

    return {
      title: `${
        companyName ? `Welcome ${companyName}, y` : "Y"
      }ou're almost in`,
      subtitle:
        "Choose the fastest way to sign in or use and eMail and password as your login.",
      imagePath: "/images/account-creation-header.png",
      bulletPoints: companyAccountCreationBulletPoints
    };
  };

  const { title, subtitle, subSubTitle, imagePath, bulletPoints } =
    buildPageTitleSubtitle();

  const accountCreationFormProps =
    accountCreationType === "company"
      ? {
          isNewCompanyAdmin: newCompanyAdminProp,
          companyId
        }
      : {
          individualData
        };

  if (accountCreationType === "company" && !isNewCompanyAdmin) {
    return (
      <Box
        style={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center"
        }}
        p={4}
      >
        <Container maxWidth="sm">
          <Paper
            style={{
              overflow: "hidden"
            }}
          >
            <Box
              style={{
                backgroundColor: palette.secondary.main,
                color: "white"
              }}
            >
              <Box p={2}>
                <Typography variant="h2" color="inherit">
                  Create Your Account
                </Typography>
              </Box>
            </Box>
            <Box p={2}>
              <Grid container direction="column" spacing={4}>
                <Grid item>
                  <Typography variant="h3">{title}</Typography>
                  <Typography variant="subtitle1" color="textSecondary">
                    {subtitle}
                  </Typography>
                  <Typography variant="subtitle2">{subSubTitle}</Typography>
                </Grid>
                <AccountCreationFlow
                  {...accountCreationFormProps}
                  setError={setErrorMsg}
                />
                {errorMsg && (
                  <Grid item>
                    <Typography color="error" variant="body1" align="center">
                      {errorMsg}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Box>
          </Paper>
        </Container>
      </Box>
    );
  }

  const accountAccessLayoutProps = {
    formTitle: title,
    formSubtitle: subtitle,
    subSubTitle,
    imagePath,
    bulletPoints,
    formDataLoading: individualDataLoading,
    accountCreationType,
    layoutType: "account-creation"
  };

  return (
    <Box
      style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center"
      }}
    >
      <AccountAccessLayout {...accountAccessLayoutProps}>
        <Container maxWidth="sm">
          <Grid container direction="column" spacing={4}>
            <AccountCreationFlow {...accountCreationFormProps} />
          </Grid>
        </Container>
      </AccountAccessLayout>
    </Box>
  );
};
export default AccountCreation;
